import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Volume2, X, Loader2, Sparkles, Check, Maximize2, Minimize2, RotateCw } from "lucide-react";

const WAKE = "silas";
const FOLLOW_UP_WINDOW_MS = 30000;
const ARMED_WINDOW_MS = 12000;
const WATCHDOG_TICK_MS = 4000;
const DEAF_AFTER_MS = 18000;

// Short two-tone chime so the bench knows the microphone came back on its own.
const chime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const at = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.14, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.11);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.12);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch (e) { /* audio unavailable */ }
};
const YES = /^(yes|yeah|yep|yup|confirm|confirmed|do it|go ahead|affirmative|correct)\b/i;
const NO = /^(no|nope|cancel|stop|never mind|nevermind|abort|negative)\b/i;

export const speechSupported = () =>
  typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

// Extracts the question after the wake word, e.g. "silas show me potts" -> "show me potts"
const afterWake = (text) => {
  const idx = text.toLowerCase().lastIndexOf(WAKE);
  if (idx === -1) return null;
  return text.slice(idx + WAKE.length).replace(/^[\s,.:;!?-]+/, "").trim();
};

// Always-on wake-word listener. The browser recognises speech locally; nothing is sent to
// PathQity until "Silas" is heard, and a visible indicator shows whenever the mic is live.
export default function SilasListener({ enabled, handsFree = false }) {
  const [live, setLive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [heard, setHeard] = useState("");
  const [state, setState] = useState("idle"); // idle | thinking | speaking
  const [armed, setArmed] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [retry, setRetry] = useState(0);
  const [size, setSize] = useState(() => localStorage.getItem("pathqity-silas-size") || "large");
  const [typed, setTyped] = useState("");
  const [closed, setClosed] = useState(() => sessionStorage.getItem("pathqity-silas-closed") === "1");
  const [alerts, setAlerts] = useState(null);
  const [micStatus, setMicStatus] = useState("off"); // off | starting | listening | reconnecting
  const [level, setLevel] = useState(0);
  const recognition = useRef(null);
  const lastAudioAt = useRef(0);
  const cycleRef = useRef(null);
  const recoveredOnce = useRef(false);
  const audio = useRef(null);
  const lastAnswerAt = useRef(0);
  const armedAt = useRef(0);
  const speaking = useRef(false);
  const sessionId = useRef(`silas-${Date.now()}`);
  const wantsOn = useRef(false);
  const pendingRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const contextCaseId = useRef("");

  useEffect(() => {
    const match = pathname.match(/^\/cases\/([^/]+)$/);
    if (match) {
      contextCaseId.current = match[1];
      localStorage.setItem("pathqity-last-case", match[1]);
    }
  }, [pathname]);

  const applySize = useCallback((next) => {
    setSize(next);
    localStorage.setItem("pathqity-silas-size", next);
  }, []);

  const shrinkForViewing = useCallback(() => {
    if (localStorage.getItem("pathqity-silas-size") === "full") applySize("large");
  }, [applySize]);

  const speak = useCallback(async (text) => {
    try {
      setState("speaking");
      speaking.current = true;
      // stop listening while Silas talks, otherwise he hears his own voice
      try { recognition.current?.stop?.(); } catch (e) { /* not running */ }
      const res = await api.post("/ai/speak", { text }, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      audio.current?.pause();
      audio.current = new Audio(url);
      const done = () => {
        speaking.current = false;
        setState("idle");
        URL.revokeObjectURL(url);
        // Edge leaves the old recogniser dead after a stop(), so build a fresh one
        if (wantsOn.current) setTimeout(() => cycleRef.current?.(false), 350);
      };
      audio.current.onended = done;
      audio.current.onerror = done;
      try {
        await audio.current.play();
      } catch (playError) {
        // browsers block audio until the page has been interacted with
        done();
        setError("Your browser blocked the audio — click anywhere on the page, then press Again.");
      }
    } catch (e) {
      speaking.current = false;
      setState("idle");
      setError(apiError(e));
      if (wantsOn.current) setTimeout(() => cycleRef.current?.(false), 350);
    }
  }, []);

  const ask = useCallback(async (question) => {
    setHeard(question);
    setState("thinking");
    setError("");
    setArmed(false);
    armedAt.current = 0;
    try {
      const { data } = await api.post("/ai/ask", {
        question,
        session_id: sessionId.current,
        context_case_id: contextCaseId.current || localStorage.getItem("pathqity-last-case") || "",
      }, { timeout: 120000 });
      setAnswer(data);
      setPending(data.pending_action || null);
      pendingRef.current = data.pending_action || null;
      lastAnswerAt.current = Date.now();
      if (data.navigate_to) {
        shrinkForViewing(); // get out of the way so the screen is visible
        navigate(data.navigate_to);
      }
      await speak(data.spoken);
    } catch (e) {
      setState("idle");
      setError(apiError(e));
    }
  }, [speak, navigate, shrinkForViewing]);

  const runPending = useCallback(async () => {
    const action = pendingRef.current;
    if (!action) return;
    setPending(null);
    pendingRef.current = null;
    setState("thinking");
    try {
      const { data } = await api.post("/ai/act", {
        action: action.action, case_id: action.case_id,
        target_stage: action.target_stage || "", comment: action.comment || "",
        block_count: action.block_count || 0, part: action.part || "A",
      }, { timeout: 60000 });
      setAnswer({ ...data, route: "action", count: 0, cases: [] });
      lastAnswerAt.current = Date.now();
      await speak(data.spoken);
    } catch (e) {
      setState("idle");
      const message = apiError(e);
      setError(message);
      speak(message);
    }
  }, [speak]);

  const cancelPending = useCallback(() => {
    setPending(null);
    pendingRef.current = null;
    setAnswer({ answer: "Cancelled — nothing was changed.", spoken: "Cancelled.", route: "action",
                count: 0, cases: [] });
  }, []);

  const listenOnce = useCallback(() => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setError("Press-to-talk needs Chrome or Edge. You can still type your question.");
      return;
    }
    setError("");
    const rec = new Recognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onstart = () => { setLive(true); setArmed(true); };
    rec.onerror = (e) => {
      setLive(false);
      setArmed(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Microphone permission was blocked. Allow the microphone in your browser's address "
                 + "bar, then press it again.");
      }
    };
    rec.onend = () => { setLive(false); setArmed(false); };
    rec.onresult = (event) => {
      const said = (event.results[0]?.[0]?.transcript || "").trim();
      if (said) ask(said).catch(() => {});
    };
    try { rec.start(); } catch (e) { /* already running */ }
  }, [ask]);

  useEffect(() => {
    if (!enabled || !handsFree || closed || muted || !speechSupported()) {
      try { recognition.current?.abort?.(); } catch (e) { /* not running */ }
      recognition.current = null;
      cycleRef.current = null;
      wantsOn.current = false;
      setLive(false);
      setMicStatus("off");
      return undefined;
    }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    wantsOn.current = true;
    let disposed = false;

    const markAudio = () => {
      lastAudioAt.current = Date.now();
      setLive(true);
      setMicStatus("listening");
      if (recoveredOnce.current) { recoveredOnce.current = false; chime(); }
    };

    const onResult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const said = (event.results[i][0]?.transcript || "").trim();
        if (!said) continue;
        // a pending action is waiting on a spoken yes or no — no wake word needed
        if (pendingRef.current) {
          const bare = said.replace(new RegExp(`^${WAKE}[\\s,.:;!?-]*`, "i"), "").trim();
          if (YES.test(bare)) { runPending().catch(() => {}); return; }
          if (NO.test(bare)) { cancelPending(); return; }
        }
        const tail = afterWake(said);
        const stillArmed = Date.now() - armedAt.current < ARMED_WINDOW_MS;
        const withinFollowUp = Date.now() - lastAnswerAt.current < FOLLOW_UP_WINDOW_MS;
        if (tail && tail.length > 1) {
          ask(tail).catch(() => {}); // name and question in one breath
        } else if (tail !== null) {
          // just the name: answer "Yes?" and wait for the question
          setArmed(true);
          armedAt.current = Date.now();
          setHeard("");
          setAnswer(null);
          speak("Yes?").catch(() => {});
        } else if ((stillArmed || withinFollowUp) && said.length > 2) {
          ask(said).catch(() => {}); // the question after "Silas", or a follow-up
        }
      }
    };
    // Builds a brand-new recogniser every time. Reusing one after stop()/an error is what
    // leaves Edge showing "listening" while the engine is actually dead.
    const cycle = (wasRecovery) => {
      if (disposed || !wantsOn.current || speaking.current) return;
      try { recognition.current?.abort?.(); } catch (e) { /* not running */ }
      recognition.current = null;
      if (wasRecovery) recoveredOnce.current = true;
      const rec = new Recognition();
      rec.continuous = true;
      rec.interimResults = false;
      rec.lang = "en-US";
      rec.onstart = () => {
        setBlocked(false);
        lastAudioAt.current = Date.now();
        setMicStatus((s) => (s === "reconnecting" ? "reconnecting" : "starting"));
      };
      rec.onaudiostart = markAudio;
      rec.onsoundstart = markAudio;
      rec.onspeechstart = markAudio;
      rec.onerror = (e) => {
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          setError("Microphone permission was blocked, so Silas cannot hear you. Allow the microphone "
                   + "in your browser's address bar, then press Retry.");
          setBlocked(true);
          wantsOn.current = false;
          setLive(false);
          setMicStatus("off");
          return;
        }
        if (e.error === "audio-capture") {
          setError("Another app or browser tab is using the microphone. Close it, then press "
                   + "Restart mic.");
        }
        // no-speech, network, aborted: the engine is finished — rebuild it on the restart below
        setLive(false);
        setMicStatus("reconnecting");
      };
      rec.onresult = (event) => { markAudio(); onResult(event); };
      rec.onend = () => {
        setLive(false);
        if (!disposed && wantsOn.current && !speaking.current) {
          setTimeout(() => cycle(micStatusWasDeaf()), 400);
        }
      };
      try { rec.start(); } catch (e) { setTimeout(() => cycle(false), 600); }
      recognition.current = rec;
    };
    const micStatusWasDeaf = () => Date.now() - lastAudioAt.current > DEAF_AFTER_MS;

    cycleRef.current = cycle;
    cycle(false);

    // Watchdog: Edge/Chrome can keep the object "running" while sending no audio at all.
    const watchdog = setInterval(() => {
      if (!wantsOn.current || speaking.current) return;
      if (Date.now() - lastAudioAt.current > DEAF_AFTER_MS) {
        setMicStatus("reconnecting");
        cycle(true);
      }
    }, WATCHDOG_TICK_MS);

    return () => {
      disposed = true;
      wantsOn.current = false;
      cycleRef.current = null;
      clearInterval(watchdog);
      try { recognition.current?.abort?.(); } catch (e) { /* not running */ }
    };
  }, [enabled, handsFree, muted, closed, ask, runPending, cancelPending, speak, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live input level so the bench can see their own voice registering.
  useEffect(() => {
    if (!enabled || !handsFree || closed || muted || !navigator.mediaDevices?.getUserMedia) {
      setLevel(0);
      return undefined;
    }
    let stream = null;
    let ctx = null;
    let raf = 0;
    let stopped = false;
    navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
      if (stopped) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(s).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) sum += buf[i];
        setLevel(Math.min(1, (sum / buf.length) / 70));
        raf = requestAnimationFrame(tick);
      };
      tick();
    }).catch(() => {});
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      ctx?.close?.().catch(() => {});
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [enabled, handsFree, closed, muted]);

  useEffect(() => () => audio.current?.pause(), []);

  // live bench board: refresh the alert counts while Silas fills the screen
  useEffect(() => {
    if (closed || size !== "full") return undefined;
    let active = true;
    const load = () => api.get("/ai/anomalies")
      .then((r) => { if (active) setAlerts(r.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [closed, size]);

  if (!enabled) return null;

  if (closed) {
    return (
      <button data-testid="silas-reopen-button"
              onClick={() => { setClosed(false); sessionStorage.removeItem("pathqity-silas-closed"); }}
              title="Bring Silas back"
              className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-teal-300 shadow-2xl hover:bg-slate-800">
        <Sparkles size={16} /> Silas
      </button>
    );
  }

  if (!speechSupported() && handsFree) {
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-xs border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-lg"
           data-testid="silas-unsupported">
        Hands-free needs Chrome or Edge — this browser cannot listen for “Silas”. The typed and
        press-to-talk assistant still works.
      </div>
    );
  }

  const busy = state === "thinking";
  const full = size === "full";
  const big = size !== "compact";
  const shellClass = full
    ? "fixed inset-0 z-[60] flex flex-col border-0 bg-slate-900 text-slate-100"
    : `fixed bottom-4 right-4 z-50 border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl ${
        big ? "w-[min(34rem,calc(100vw-2rem))]" : "w-[min(22rem,calc(100vw-2rem))]"}`;
  const bodyText = full ? "text-xl" : big ? "text-base" : "text-sm";
  const hintText = full ? "text-base" : big ? "text-sm" : "text-xs";
  const answerText = full ? "text-2xl leading-relaxed" : big ? "text-lg" : "text-sm";
  const cycleSize = () => {
    const next = size === "compact" ? "large" : size === "large" ? "full" : "compact";
    applySize(next);
  };

  return (
    <div className={shellClass} data-testid="silas-panel" data-size={size}>
      <header className={`flex items-center gap-2 border-b border-slate-700 px-3 ${full ? "py-4" : "py-2"}`}>
        <Sparkles size={full ? 22 : 15} className="text-teal-300" />
        <span className={`font-semibold ${full ? "text-2xl" : "text-sm"}`}>Silas</span>
        {muted ? (
          <span className={`inline-flex items-center gap-1 text-slate-400 ${hintText}`} data-testid="silas-muted">
            <MicOff size={full ? 16 : 12} /> muted
          </span>
        ) : !handsFree ? (
          <span className={`inline-flex items-center gap-1 text-slate-400 ${hintText}`} data-testid="silas-typed-only">
            <MicOff size={full ? 16 : 12} /> {busy ? "thinking" : state === "speaking" ? "speaking" : "type or press to talk"}
          </span>
        ) : (
          <span className={`inline-flex items-center gap-1.5 text-teal-300 ${hintText}`} data-testid="silas-listening">
            <span className={`h-2 w-2 rounded-full ${
              micStatus === "reconnecting" ? "bg-amber-400 animate-pulse" : "bg-teal-400"
            } ${live || micStatus === "reconnecting" ? "animate-pulse" : "opacity-40"}`} />
            {busy ? "thinking" : state === "speaking" ? "speaking"
              : micStatus === "reconnecting" ? "reconnecting mic…"
              : armed ? "ready — ask now"
              : micStatus === "listening" ? "listening for “Silas”" : "starting"}
            <span className="ml-1 inline-flex h-2 w-10 overflow-hidden rounded-full bg-slate-700"
                  data-testid="silas-mic-level" title="Live microphone level">
              <span className="h-full bg-emerald-400 transition-[width] duration-75"
                    style={{ width: `${Math.round(level * 100)}%` }} />
            </span>
          </span>
        )}
        {handsFree && !muted && (
          <button onClick={() => { lastAudioAt.current = 0; setError(""); setRetry((n) => n + 1); }}
                  data-testid="silas-restart-mic-button"
                  className={`inline-flex items-center gap-1 rounded border border-slate-600 px-2 py-1 text-slate-300 hover:bg-white/10 ${hintText}`}
                  title="Rebuild the microphone connection">
            <RotateCw size={full ? 16 : 12} /> Restart mic
          </button>
        )}
        <button onClick={cycleSize} data-testid="silas-size-button"
                className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded text-slate-300 hover:bg-white/10"
                title={full ? "Shrink to the corner" : size === "large" ? "Fill the screen" : "Make it bigger"}>
          {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        <button onClick={() => setMuted((m) => !m)} data-testid="silas-mute-button"
                className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-300 hover:bg-white/10"
                title={muted ? "Resume listening" : "Mute the microphone"}>
          {muted ? <Mic size={full ? 18 : 14} /> : <MicOff size={full ? 18 : 14} />}
        </button>
        <button data-testid="silas-close-button"
                onClick={() => {
                  audio.current?.pause();
                  setClosed(true);
                  sessionStorage.setItem("pathqity-silas-closed", "1");
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-300 hover:bg-white/10 hover:text-white"
                title="Close Silas — the microphone stops until you reopen him">
          <X size={full ? 20 : 16} />
        </button>
      </header>

      <div className={`px-3 ${full ? "flex-1 overflow-y-auto py-5 sm:px-10" : "py-2"} ${bodyText}`}>
        {!heard && !error && (
          <p className={`text-slate-400 ${hintText}`} data-testid="silas-hint">
            {handsFree ? (
              <>
                Say <span className="font-semibold text-teal-300">“Silas”</span> then your question — for
                example “Silas, brief me”, “Silas, which cases are over turnaround?” or
                “Silas, open TechTraX”.
              </>
            ) : (
              <>
                Ask me anything about the lab — type below or press the microphone. To call me by name
                without touching anything, turn on hands-free in{" "}
                <Link to="/account" className="font-semibold text-teal-300 underline"
                      data-testid="silas-handsfree-link">your account settings</Link>.
              </>
            )}
          </p>
        )}
        {heard && (
          <p className={`italic text-slate-400 ${hintText}`} data-testid="silas-heard">“{heard}”</p>
        )}
        {armed && !busy && state !== "speaking" && (
          <p className={`mt-1 font-semibold text-teal-300 ${hintText}`} data-testid="silas-armed">
            Listening — ask your question
          </p>
        )}
        {blocked && (
          <Button size="sm" data-testid="silas-retry-button"
                  onClick={() => { setBlocked(false); setError(""); setRetry((n) => n + 1); }}
                  className={`mt-2 bg-teal-500 font-bold text-slate-950 hover:bg-teal-400 ${full ? "h-12 px-4 text-base" : "h-7 px-2 text-xs"}`}>
            Retry microphone
          </Button>
        )}
        {!blocked && (
          <form className="mt-2 flex gap-2"
                onSubmit={(e) => { e.preventDefault(); const q = typed.trim(); if (q) { setTyped(""); ask(q); } }}>
            <input value={typed} onChange={(e) => setTyped(e.target.value)}
                   data-testid="silas-typed-input" placeholder="…or type it here"
                   className={`min-w-0 flex-1 border border-slate-600 bg-slate-800 px-2 text-slate-100 placeholder:text-slate-500 ${full ? "h-12 text-base" : "h-8 text-sm"}`} />
            <Button type="submit" size="sm" data-testid="silas-typed-submit"
                    className={`bg-teal-500 font-bold text-slate-950 hover:bg-teal-400 ${full ? "h-12 px-5 text-base" : "h-8 px-3 text-xs"}`}>
              Ask
            </Button>
          </form>
        )}
        {!blocked && (
          <Button size="sm" variant="outline" data-testid="silas-push-to-talk"
                  onClick={() => {
                    if (handsFree) { setArmed(true); armedAt.current = Date.now(); setError(""); }
                    else listenOnce();
                  }}
                  className={`mt-2 border-slate-600 bg-transparent text-slate-200 hover:bg-white/10 ${full ? "h-12 px-4 text-base" : "h-7 px-2 text-xs"}`}>
            <Mic size={full ? 16 : 12} className="mr-1" />
            {handsFree ? "Ask without the wake word" : live ? "Listening — ask now" : "Press to talk"}
          </Button>
        )}
        {busy && (
          <p className={`mt-2 inline-flex items-center gap-2 text-slate-300 ${hintText}`}>
            <Loader2 size={full ? 18 : 13} className="animate-spin" /> Looking it up…
          </p>
        )}
        {pending && (
          <div className={`mt-2 border-l-4 border-amber-400 bg-amber-500/10 ${full ? "p-4" : "p-2"}`} data-testid="silas-pending-action">
            <p className={`font-semibold text-amber-200 ${full ? "text-xl" : big ? "text-base" : "text-xs"}`}>
              {pending.action === "advance_stage"
                ? `Advance ${pending.accession_number} from ${pending.current_stage} to ${pending.target_stage}?`
                : pending.action === "create_blocks"
                ? `Log ${pending.block_count} cassette(s) from part ${pending.part} on ${pending.accession_number}?`
                : `Add a comment to ${pending.accession_number}?`}
            </p>
            {pending.comment && <p className={`mt-1 italic text-slate-300 ${hintText}`}>“{pending.comment}”</p>}
            <p className={`mt-1 text-slate-400 ${hintText}`}>Say “yes” to confirm or “no” to cancel.</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={runPending} data-testid="silas-confirm-button"
                      className={`bg-teal-500 font-bold text-slate-950 hover:bg-teal-400 ${full ? "h-14 px-8 text-lg" : "h-7 px-2 text-xs"}`}>
                <Check size={full ? 20 : 12} className="mr-1" /> Yes
              </Button>
              <Button size="sm" variant="outline" onClick={cancelPending} data-testid="silas-cancel-button"
                      className={`border-slate-600 bg-transparent text-slate-200 hover:bg-white/10 ${full ? "h-14 px-8 text-lg" : "h-7 px-2 text-xs"}`}>
                <X size={full ? 20 : 12} className="mr-1" /> No
              </Button>
            </div>
          </div>
        )}
        {answer && !busy && (
          <div className="mt-2" data-testid="silas-answer">
            <p className={`text-slate-100 ${answerText}`}>{answer.answer}</p>
            {answer.count > 0 && (
              <ul className={`mt-2 space-y-1 overflow-y-auto border-t border-slate-700 pt-2 ${full ? "max-h-[55vh]" : "max-h-40"}`}>
                {answer.cases.slice(0, full ? 50 : 8).map((c) => (
                  <li key={c.case_id} className={full ? "text-lg" : big ? "text-sm" : "text-xs"}>
                    <Link to={`/cases/${c.case_id}`} onClick={shrinkForViewing}
                          data-testid={`silas-case-row-${c.accession_number}`}
                          className="flex items-baseline gap-2 rounded px-1 py-0.5 hover:bg-white/10">
                      <span className="font-mono font-semibold text-teal-300">{c.accession_number}</span>
                      <span className="text-slate-300">{c.patient_name}</span>
                      <span className="ml-auto text-slate-500">{c.status}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => speak(answer.spoken)}
                      data-testid="silas-replay-button"
                      className={`border-slate-600 bg-transparent text-slate-200 hover:bg-white/10 ${full ? "h-12 px-4 text-base" : "h-7 px-2 text-xs"}`}>
                <Volume2 size={full ? 16 : 12} className="mr-1" /> Again
              </Button>
              <button onClick={() => { setAnswer(null); setHeard(""); }} data-testid="silas-clear-button"
                      className={`inline-flex items-center gap-1 text-slate-400 hover:text-white ${hintText}`}>
                <X size={full ? 16 : 12} /> Clear
              </button>
              <span className={`ml-auto uppercase tracking-wide text-slate-500 ${full ? "text-xs" : "text-[10px]"}`}>
                {answer.route === "knowledge" ? "from lab records"
                  : answer.route === "action" ? "voice action — logged in the audit trail"
                  : answer.route === "navigate" ? "opening a screen"
                  : answer.route === "data" ? "from case data" : "silas"}
              </span>
            </div>
          </div>
        )}
        {error && <p className={`mt-2 text-amber-300 ${hintText}`} data-testid="silas-error">{error}</p>}

        {full && alerts && (
          <section className="mt-8 border-t border-slate-700 pt-6" data-testid="silas-board">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-xl font-semibold text-slate-100">Bench status</h2>
              <span className="text-sm text-slate-500" data-testid="silas-board-updated">
                live · refreshed {new Date(alerts.generated_at).toLocaleTimeString()}
              </span>
              <span className="ml-auto text-sm text-slate-400">
                targets {alerts.targets?.routine_hours}h routine · {alerts.targets?.stat_hours}h STAT
              </span>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {alerts.groups.map((group) => (
                <div key={group.kind}
                     data-testid={`silas-board-${group.kind}`}
                     className={`border-l-4 bg-slate-800/60 p-4 ${
                       group.total === 0 ? "border-slate-600"
                         : group.severity === "critical" ? "border-red-500" : "border-amber-400"}`}>
                  <p className={`text-4xl font-bold tabular-nums ${
                    group.total === 0 ? "text-slate-500"
                      : group.severity === "critical" ? "text-red-400" : "text-amber-300"}`}>
                    {group.total}
                  </p>
                  <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-slate-300">
                    {group.title}
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {group.items.slice(0, 6).map((item, i) => (
                      <li key={item.case_id || `${group.kind}-${i}`} className="text-base">
                        {item.case_id ? (
                          <Link to={`/cases/${item.case_id}`} onClick={shrinkForViewing}
                                data-testid={`silas-board-item-${item.accession_number}`}
                                className="flex flex-wrap items-baseline gap-x-2 rounded px-1 hover:bg-white/10">
                            <span className="font-mono font-semibold text-teal-300">{item.accession_number}</span>
                            <span className="text-slate-300">{item.patient_name}</span>
                            <span className="text-sm text-slate-400">{item.detail}</span>
                          </Link>
                        ) : (
                          <span className="text-slate-300">
                            {item.stage} — {item.case_count} cases, oldest {item.oldest_hours}h
                          </span>
                        )}
                      </li>
                    ))}
                    {group.total > 6 && (
                      <li className="text-sm text-slate-500">and {group.total - 6} more</li>
                    )}
                    {group.total === 0 && <li className="text-base text-slate-500">All clear</li>}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
